import { Badge, Box, Button, Divider, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconArrowBackUp,
  IconChartBar,
  IconCoins,
  IconCurrencyTugrik,
  IconHistory,
  IconPencil,
  IconScissors,
  IconTag,
  IconUsers,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { useEditPolicy } from "../../hooks/useEditPolicy";
import { formatDate, formatMoney, formatPercent } from "../../utils/format";
import {
  calcMethodLabel,
  deductionModeLabel,
  feeBasisLabel,
  formatTierRange,
  formatTierRate,
  headlineLabel,
  readTiers,
  refundPolicyLabel,
} from "../../utils/fee-policy-labels";
import { FeePolicyStatusBadge } from "./FeePolicyStatusBadge";

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };
const heroBorder = { borderColor: "var(--mantine-color-brand-4)" };

interface Props {
  /** Most recent ACTIVE DEFAULT policy. */
  policy: FeePolicy | null;
  /** Count of customers using DEFAULT (no assignment). May be undefined while loading. */
  customerCount?: number;
}

function SettingRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <ThemeIcon variant="light" color="brand" size="sm" radius="sm">{icon}</ThemeIcon>
      <Box>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
      </Box>
    </Group>
  );
}

export function PolicyDefaultCard({ policy, customerCount }: Props) {
  const navigate = useNavigate();
  const editPolicy = useEditPolicy();

  if (!policy) {
    return (
      <Paper p="lg" withBorder radius="md" style={{ ...heroWash, borderColor: "var(--mantine-color-red-4)" }}>
        <Stack gap={4}>
          <Group gap="xs">
            <Badge color="brand" size="md" radius="sm">Үндсэн</Badge>
            <Text fw={600} c="red.7">Идэвхтэй DEFAULT бодлого алга — үүсгэнэ үү</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Бүх харилцагчид хэрэглэгдэх суурь бодлого хараахан тохируулаагүй байна. Шинэ ноорог үүсгэж идэвхжүүлнэ үү.
          </Text>
        </Stack>
      </Paper>
    );
  }

  const isTiered = policy.calculationMethod === "TIERED";
  const tiers = isTiered ? readTiers(policy) : [];
  const headline =
    policy.calculationMethod === "PERCENTAGE" && policy.feeRate
      ? formatPercent(Number(policy.feeRate) * 100)
      : policy.calculationMethod === "FLAT" && policy.flatFeeAmount
      ? formatMoney(policy.flatFeeAmount, policy.feeCurrency)
      : calcMethodLabel(policy.calculationMethod);

  const view = () => navigate(`/my-billing/fee-policy/${policy.id}`);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Paper
      p="lg"
      withBorder
      radius="md"
      style={{ ...heroWash, ...heroBorder, cursor: "pointer" }}
      onClick={view}>
      <Group justify="space-between" align="flex-start" mb="md">
        <Group gap="xs">
          <Badge color="brand" size="md" radius="sm">Үндсэн</Badge>
          <FeePolicyStatusBadge status={policy.status} />
          {policy.currentVersion ? (
            <Text size="sm" c="dimmed" fw={600}>v{policy.currentVersion}</Text>
          ) : null}
          {policy.effectiveFrom ? (
            <Text size="xs" c="dimmed">{formatDate(policy.effectiveFrom)}-ээс</Text>
          ) : null}
        </Group>
        <Group gap="xs" onClick={stop}>
          <Button size="compact-sm" variant="subtle" leftSection={<IconHistory size={16} />}
            onClick={() => navigate("/my-billing/fee-policy/history")}>
            Түүх
          </Button>
          <Button size="compact-sm" variant="light" leftSection={<IconPencil size={16} />}
            onClick={() => editPolicy(policy)}>
            Засах
          </Button>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Stack gap="sm">
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
              {headlineLabel(policy.calculationMethod)}
            </Text>
            {isTiered && tiers.length > 0 ? (
              <Stack gap={4} mt={4}>
                {tiers.map((t, i) => (
                  <Group key={i} justify="space-between" wrap="nowrap" gap="sm">
                    <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatTierRange(t, policy.feeCurrency)}
                    </Text>
                    <Text size="md" fw={700} c="brand.7" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatTierRate(t)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Title order={1} c="brand.7" style={{ fontVariantNumeric: "tabular-nums" }}>{headline}</Title>
            )}
          </Box>
          <Paper p="sm" radius="md" withBorder bg="light-dark(var(--mantine-color-white), var(--mantine-color-dark-5))">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color="brand" radius="xl"><IconUsers size={18} /></ThemeIcon>
              <Box>
                <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                  {customerCount !== undefined ? `${customerCount} харилцагч` : "—"}
                </Text>
                <Text size="xs" c="dimmed">бүгд автоматаар (тусгай оноолтгүй)</Text>
              </Box>
            </Group>
          </Paper>
        </Stack>

        <Stack gap="sm">
          <SettingRow
            icon={<IconCoins size={14} />}
            label="Доод / Дээд"
            value={`${policy.minFeeAmount ? formatMoney(policy.minFeeAmount, policy.feeCurrency) : "—"} – ${
              policy.maxFeeAmount ? formatMoney(policy.maxFeeAmount, policy.feeCurrency) : "—"
            }`}
          />
          <Divider />
          <SettingRow icon={<IconChartBar size={14} />} label="Тооцох суурь" value={feeBasisLabel(policy.feeBasis)} />
          <Divider />
          <SettingRow icon={<IconScissors size={14} />} label="Цуглуулах арга" value={deductionModeLabel(policy.deductionMode)} />
          <Divider />
          <SettingRow icon={<IconArrowBackUp size={14} />} label="Буцаалт" value={refundPolicyLabel(policy.refundPolicy)} />
          <Divider />
          <SettingRow icon={<IconTag size={14} />} label="Платформ шимтгэл" value={policy.absorbPlatformFee ? "Өөрөө үүрнэ" : "Өөрөө үүрэхгүй"} />
          <Divider />
          <SettingRow icon={<IconCurrencyTugrik size={14} />} label="Валют" value={policy.feeCurrency} />
        </Stack>
      </SimpleGrid>
    </Paper>
  );
}
